import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../shared/apiClient';
import { Bell, ChevronRight, X } from 'lucide-react';

interface ImpiantoRef {
  id: string;
  codice: string;
  nome: string;
}

interface AvvisoItem {
  id: string;
  testo: string;
  attivo: boolean;
  inizioAt?: string | null;
  fineAt?: string | null;
  creatoDa?: string;
  createdAt: string;
  impianti: ImpiantoRef[];
  isGenerale: boolean;
}

// Sfondi scuri ad alto contrasto con testo bianco, leggibili sulla barra ambra
const PALETTE_AVVISI = ['#0f172a', '#1d4ed8', '#047857', '#6d28d9', '#be123c'];

export function AvvisiTicker() {
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!modalOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [modalOpen]);

  // Polling avvisi attivi ogni 20 secondi
  const { data: avvisi = [] } = useQuery<AvvisoItem[]>({
    queryKey: ['avvisi-impianti-attivi'],
    queryFn: async () => {
      const { data } = await apiGet<AvvisoItem[]>('/avvisi-impianti', { soloAttivi: 'true' });
      return Array.isArray(data) ? data : [];
    },
    refetchInterval: 20000,
  });

  if (!avvisi || avvisi.length === 0) {
    return null;
  }

  // Ogni avviso è una pillola con sfondo proprio (palette ciclica) + contatore "n/N" e separatore visibile,
  // così inizio e fine di ciascun avviso restano distinguibili anche quando scorrono in sequenza.
  const renderAvvisi = (copia: string) =>
    avvisi.map((a, i) => {
      const colore = PALETTE_AVVISI[i % PALETTE_AVVISI.length];
      const impStr = (a.impianti || []).map((imp) => `[${imp.codice}] ${imp.nome}`).join(' · ');
      const generale = a.isGenerale || !a.impianti || a.impianti.length === 0;
      return (
        <span key={`${copia}-${a.id}`} className="inline-flex items-center align-middle">
          <span
            className="inline-flex items-center gap-2 rounded-lg px-3 py-1 font-semibold shadow-sm"
            style={{ background: colore, color: '#ffffff' }}
          >
            {avvisi.length > 1 && (
              <span className="rounded bg-white/25 px-1.5 text-[10px] font-extrabold leading-4">
                {i + 1}/{avvisi.length}
              </span>
            )}
            <span>
              {generale ? '📢' : '⚠️'} {a.testo}
              {!generale && `: ${impStr}`}
            </span>
          </span>
          <span aria-hidden="true" className="mx-4 text-slate-950/70 text-[10px]">◆</span>
        </span>
      );
    });

  return (
    <>
      {/* Ticker Bar fissa sopra la bottom bar (posizionata dinamicamente sopra la bottom nav + safe-area) */}
      <div
        onClick={() => setModalOpen(true)}
        className="fixed bottom-[calc(58px+env(safe-area-inset-bottom,0px))] left-0 right-0 z-30 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-xs py-2 px-3 shadow-lg cursor-pointer transition select-none flex items-center gap-2 border-t border-b border-amber-600/30"
        title="Tocca per visualizzare l'elenco completo degli avvisi"
      >
        <div className="flex items-center gap-1.5 shrink-0 bg-slate-950/15 px-2 py-0.5 rounded-md text-[11px] font-extrabold tracking-wide uppercase">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-slate-950 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-950"></span>
          </span>
          <span>Avvisi ({avvisi.length})</span>
        </div>

        {/* Contenitore a scorrimento infinito */}
        <div className="overflow-hidden whitespace-nowrap flex-1 relative flex items-center">
          <div className="inline-block animate-marquee hover:[animation-play-state:paused]">
            <span>{renderAvvisi('a')}</span>
            <span aria-hidden="true">{renderAvvisi('b')}</span>
          </div>
        </div>

        <div className="shrink-0 flex items-center text-slate-900 bg-white/40 hover:bg-white/60 px-1.5 py-0.5 rounded text-[10px] font-semibold gap-0.5">
          <span>Vedi</span>
          <ChevronRight className="w-3 h-3" />
        </div>
      </div>

      {/* Modale con l'elenco fermo e leggibile di tutti gli avvisi */}
      {modalOpen && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-slate-950/60 backdrop-blur-xs animate-fade-in safe-bottom">
          <div className="bg-white w-full max-w-lg rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[85dvh] sm:max-h-[85vh]">
            {/* Header modale */}
            <div className="p-4 bg-amber-500 text-slate-950 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 fill-slate-950" />
                <h3 className="font-extrabold text-base">Avvisi di Centrale ({avvisi.length})</h3>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="p-1.5 rounded-xl hover:bg-black/10 transition text-slate-950"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Lista avvisi dettagliati */}
            <div className="p-4 overflow-y-auto flex-1 space-y-3 divide-y divide-slate-100">
              {avvisi.map((a, idx) => (
                <div key={a.id} className={idx > 0 ? 'pt-3' : ''}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
                      {a.isGenerale ? '📢 Avviso Generale' : `📍 Impianti (${a.impianti.length})`}
                    </span>
                    <span className="text-[10px] text-slate-400">
                      {new Date(a.createdAt).toLocaleDateString('it-IT', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>

                  <p className="text-sm font-bold text-slate-900 mb-1 leading-snug">
                    {a.testo}
                  </p>

                  {/* Finestra temporale se specificata */}
                  {(a.inizioAt || a.fineAt) && (
                    <div className="text-[11px] text-slate-500 mb-2 font-medium">
                      {a.inizioAt && <span>Dal: {new Date(a.inizioAt).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })} </span>}
                      {a.fineAt && <span>al: {new Date(a.fineAt).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}</span>}
                    </div>
                  )}

                  {/* Impianti collegati */}
                  {a.impianti && a.impianti.length > 0 && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 space-y-1.5">
                      <div className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">
                        Impianti interessati:
                      </div>
                      <div className="flex flex-col gap-1.5">
                        {a.impianti.map((imp) => (
                          <div
                            key={imp.id}
                            className="flex items-start text-xs bg-white border border-amber-300 text-slate-800 p-1.5 rounded-lg font-medium shadow-2xs gap-2"
                          >
                            <span className="font-mono font-bold text-amber-900 bg-amber-50 px-1.5 py-0.5 rounded text-[11px] shrink-0">
                              [{imp.codice}]
                            </span>
                            <span className="break-words leading-relaxed flex-1 text-slate-900 font-semibold">{imp.nome}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Footer modale */}
            <div className="p-3 bg-slate-50 border-t border-slate-200 flex justify-end shrink-0">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="btn-primary text-xs py-2 px-5 bg-slate-900 hover:bg-slate-800"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
